<template>
  <Transition name="slide-up">
    <div v-if="minimizedDeliverables.length > 0" class="minimized-deliverables-toolbar">
      <div class="toolbar-content">
        <!-- Minimized deliverable items -->
        <div class="minimized-deliverables">
          <div
            v-for="deliverable in minimizedDeliverables"
            :key="deliverable.id"
            class="tag minimized-deliverable-item"
            :class="getDeliverableTypeClassName(deliverable.type)"
            @click="handleRestoreDeliverable(deliverable)"
          >
            <!-- Status indicator -->
            <span
              class="status-dot"
              :style="{ backgroundColor: getStatusColor(deliverable.status) }"
              :title="getStatusLabel(deliverable.status)"
            ></span>

            <!-- Type icon -->
            <span class="icon is-small">
              <font-awesome-icon :icon="['fal', getTypeIcon(deliverable.type)]" />
            </span>

            <!-- Deliverable title -->
            <span class="deliverable-title">{{ deliverable.title }}</span>

            <!-- Due date if present -->
            <span v-if="deliverable.dueDate" class="due-date">
              <span class="icon is-small">
                <font-awesome-icon :icon="['fal', 'calendar']" />
              </span>
              <span>{{ formatDate(deliverable.dueDate) }}</span>
            </span>

            <!-- Close button -->
            <span
              class="delete is-small"
              @click.stop="handleCloseDeliverable(deliverable.id)"
              title="Close deliverable"
            ></span>
          </div>
        </div>

        <!-- Toolbar actions -->
        <div class="toolbar-actions">
          <button
            class="button is-small is-text"
            @click="restoreAll"
            title="Close all minimized deliverables"
          >
            <span class="icon">
              <font-awesome-icon :icon="['fal', 'times']" />
            </span>
            <span>Close All</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { watch, onUnmounted, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useAdminDeliverablesStore } from '@/stores/adminDeliverablesStore';
import {
  getDeliverableStatusColor,
  getDeliverableStatusLabel,
  getDeliverableTypeIcon,
  getDeliverableTypeClassName
} from '@/utils/deliverableUtils';
import { logger } from '@/services/loggerService';

// Emits
const emit = defineEmits(['restore']);

// Store
const adminDeliverablesStore = useAdminDeliverablesStore();
const { minimizedDeliverables } = storeToRefs(adminDeliverablesStore);

// Methods
const getStatusColor = (status) => getDeliverableStatusColor(status);
const getStatusLabel = (status) => getDeliverableStatusLabel(status);
const getTypeIcon = (type) => getDeliverableTypeIcon(type);

const formatDate = (date) => {
  if (!date) return '';

  const d = date.toDate ? date.toDate() : new Date(date);
  const now = new Date();
  const diff = d - now;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days < 0) {
    return `${Math.abs(days)}d overdue`;
  } else if (days === 0) {
    return 'Today';
  } else if (days === 1) {
    return 'Tomorrow';
  } else if (days < 7) {
    return `${days}d`;
  } else {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const handleRestoreDeliverable = (deliverable) => {
  logger.info('[MinimizedDeliverablesToolbar] Restoring deliverable', {
    deliverableId: deliverable.id,
    title: deliverable.title
  });

  // Emit restore event to parent to open the modal
  // The parent will handle removing from minimized list after opening
  emit('restore', deliverable);
};

const handleCloseDeliverable = (deliverableId) => {
  logger.info('[MinimizedDeliverablesToolbar] Closing deliverable', {
    deliverableId
  });

  // Remove from minimized list without restoring
  adminDeliverablesStore.restoreDeliverable(deliverableId);
};

const restoreAll = () => {
  // Clear all minimized deliverables
  const count = minimizedDeliverables.value.length;
  adminDeliverablesStore.clearMinimized();

  logger.info('[MinimizedDeliverablesToolbar] Cleared all minimized deliverables', {
    count
  });
};

// Main element margin management to prevent content from being covered
const TOOLBAR_HEIGHT = 80; // Approximate height of toolbar in pixels

const updateMainMargin = (hasMinimized) => {
  const mainElement = document.querySelector('main');
  if (!mainElement) return;

  if (hasMinimized) {
    mainElement.style.marginBottom = `${TOOLBAR_HEIGHT}px`;
  } else {
    mainElement.style.marginBottom = '';
  }
};

// Watch for changes in minimized deliverables
watch(
  () => minimizedDeliverables.value.length > 0,
  (hasMinimized) => {
    nextTick(() => {
      updateMainMargin(hasMinimized);
    });
  },
  { immediate: true }
);

// Cleanup on unmount
onUnmounted(() => {
  const mainElement = document.querySelector('main');
  if (mainElement) {
    mainElement.style.marginBottom = '';
  }
});
</script>

<style scoped lang="scss">
// Minimized Deliverables Toolbar
.minimized-deliverables-toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bulma-scheme-main);
  border-top: 1px solid var(--bulma-border);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 30; // Below modals (z-index: 40) so it can't be clicked when modal is open
  transition: all 0.3s ease;

  .toolbar-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
    gap: 1rem;
  }

  .minimized-deliverables {
    display: flex;
    gap: 0.5rem;
    flex: 1;
    overflow-x: auto;
    overflow-y: visible;
    padding: 0.25rem 0;

    &::-webkit-scrollbar {
      height: 6px;
    }

    &::-webkit-scrollbar-track {
      background: var(--bulma-scheme-main-ter);
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: var(--bulma-border);
      border-radius: 3px;

      &:hover {
        background: var(--bulma-border-hover);
      }
    }
  }

  .minimized-deliverable-item {
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    height: auto !important;
    font-size: 0.875rem !important;
    padding: 0.5rem 0.75rem !important;
    gap: 0.5rem;
    display: inline-flex !important;
    align-items: center;
    margin-right: 0.5rem; // Add spacing between items

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
      z-index: 10; // Ensure hover item is above others
    }

    .status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .deliverable-title {
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 250px;
      flex: 1;
    }

    .due-date {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.75rem;
      opacity: 0.8;
      white-space: nowrap;
    }

    .delete {
      margin-left: 0.5rem;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .toolbar-actions {
    display: flex;
    gap: 0.5rem;
  }
}

// Slide up animation
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-up-enter-from {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>